import axios from "axios";
import { useEffect, useState } from "react";
import { MdArrowBackIos } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { setRecipient } from "redux/slices/tradevuTransferSlice";
import { setBankRecipient } from "redux/slices/bankTransferSlice";
import ChooseBankModal from "./ChooseBankModal";
import {
    setCryptoAddress,
    setCryptoNetwork,
    selectCryptoTransfer,
} from "../../redux/slices/cryptoTransferSlice";
import SelectNetworkModal from "./SelectNetworkModal";
import { setCryptoFiatRecipient } from "../../redux/slices/cryptoFiatTransferSlice";
import { setUsdAll } from "../../redux/slices/usdTransferSlice";
import Input from "component/form/Input";
import {
    BankPickerCard,
    UsdIbanSection,
    UsdSwiftSection,
    UsdAchSection,
    UsdRoutingNumberField,
    NonUsdRecipientSection,
    CryptoRecipientSection,
} from "./RecipientUtils";
import useUsdRoutingVerification from "hooks/useUsdRoutingVerification";

export default function AddRecipientForm({ onBack, nextRoute }) {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const { network: savedNetwork, address: savedAddress } = useSelector(selectCryptoTransfer);
    const cryptoFiat = useSelector((s) => s.cryptoFiatTransfer);
    const usdTx = useSelector((s) => s.usdTransfer);

    const { business, currency } = useSelector((state) => state);
    const bankProvider = useSelector((state) => state.bankProvider);
    const provider = useSelector((state) => state.provider);

    const [showNetworkModal, setShowNetworkModal] = useState(false);
    const [cryptoNetwork, setCryptoNetworkLocal] = useState(savedNetwork || "ERC20");
    const [cryptoAddress, setCryptoAddressLocal] = useState(savedAddress || "");
    const [accountNumber, setAccountNumber] = useState("");
    const [accountName, setAccountName] = useState("");
    const [bankName, setBankName] = useState(usdTx?.bankName || "");
    const [loading, setLoading] = useState(false);
    const [showBankModal, setShowBankModal] = useState(false);
    const [selectedBank, setSelectedBank] = useState(null);
    const [swiftCode, setSwiftCode] = useState("");
    const [accountType, setAccountType] = useState("");
    const [routingNumber, setRoutingNumber] = useState(usdTx?.account?.routingNumber || "");



    const isUSD = currency === "USD";
    const checkingOrSavings = accountType || "";
    const recipientType = sessionStorage.getItem("recipientType") || "US";
    const selectedPaymentRail =
        usdTx?.paymentRail || sessionStorage.getItem("paymentRail") || "ach";
    const isIBANFlow = isUSD && recipientType === "NON_US";
    const isSwiftFlow =
        isUSD &&
        (recipientType === "SWIFT" ||
            recipientType === "INTERNATIONAL_WIRE" ||
            (recipientType === "US" && selectedPaymentRail === "swift"));

    const [bankCountryCode, setBankCountryCode] = useState("");
    const [iban, setIban] = useState("");
    const [bic, setBic] = useState("");
    const [holderType, setHolderType] = useState(""); // 'individual' | 'corporate'
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [businessName, setBusinessName] = useState("");

    const COUNTRY_OPTIONS = [
        { value: "DE", label: "Germany" },
        { value: "GB", label: "United Kingdom" },
        { value: "FR", label: "France" },
        { value: "NL", label: "Netherlands" },
        { value: "IE", label: "Ireland" },
        // …extend
    ];

    // USD recipient address fields
    const [recStreet, setRecStreet] = useState("");
    const [recCountry, setRecCountry] = useState("US");
    const [recCity, setRecCity] = useState("");
    const [recState, setRecState] = useState("");
    const [recZip, setRecZip] = useState("");
    const [bankStreet, setBankStreet] = useState("");
    const [bankCountry, setBankCountry] = useState("US");
    const [bankCity, setBankCity] = useState("");
    const [bankState, setBankState] = useState("");
    const [bankZip, setBankZip] = useState("");
    const [relationship, setRelationship] = useState("");
    const [recipientEmail, setRecipientEmail] = useState("");

    const savedInternal = useSelector((s) => s.recipient);
    const savedBank = useSelector((s) => s.bankRecipient);
    const savedCryptoFiat = useSelector((s) => s.cryptoFiatTransfer?.recipient);

    const isBankRecipient = location.pathname.includes("bank-recipient");
    const isCryptoFlow = location.pathname.includes("crypto-wallet");
    const isCryptoToFiatFlow = location.pathname.includes("fiat-wallet");
    const isCryptoCurrency = currency === "USDC" || currency === "USDT";
    const MIN_DIGITS = 9;
    const MAX_DIGITS = 15;
    const businessId = business?._id;
    const recipientCurrency = cryptoFiat.recipient.toCurrency;
    const bankFormCurrency = isCryptoToFiatFlow ? recipientCurrency : currency;
    const validationCurrencyFrom = String(
        isCryptoToFiatFlow ? cryptoFiat?.currency || currency : currency
    ).toUpperCase();
    const validationCurrencyTo = String(
        isCryptoToFiatFlow ? recipientCurrency || currency : currency
    ).toUpperCase();
    const verificationSource =
        bankProvider?.name ||
        provider?.name ||
        selectedBank?.source ||
        selectedBank?.provider ||
        "";
    const isKES = String(bankFormCurrency || "").toUpperCase() === "KES";
    const {
        details: verifiedRoutingDetails,
        error: routingVerificationError,
        loading: routingVerificationLoading,
    } = useUsdRoutingVerification({
        businessId,
        currency: validationCurrencyFrom,
        currencyTo: validationCurrencyTo,
        source: verificationSource,
        enabled: isUSD && !isIBANFlow,
        routingNumber,
    });

    const recipientLocationLabel = isIBANFlow
        ? "Recipient outside the US"
        : "Recipient in the US";
    const paymentMethodLabel = isIBANFlow
        ? "IBAN / SWIFT"
        : isSwiftFlow
          ? "International wire transfer"
          : "ACH";

    const openNetworkPicker = () => setShowNetworkModal(true);
    const closeNetworkPicker = () => setShowNetworkModal(false);

    useEffect(() => {
        if (!verifiedRoutingDetails) return;

        if (verifiedRoutingDetails.accountName) {
            setAccountName(verifiedRoutingDetails.accountName);
        }
        if (verifiedRoutingDetails.bankName) setBankName(verifiedRoutingDetails.bankName);
        if (verifiedRoutingDetails.bic) setBic(verifiedRoutingDetails.bic);
        if (verifiedRoutingDetails.bankStreet) setBankStreet(verifiedRoutingDetails.bankStreet);
        if (verifiedRoutingDetails.bankCity) setBankCity(verifiedRoutingDetails.bankCity);
        if (verifiedRoutingDetails.bankState) setBankState(verifiedRoutingDetails.bankState);
        if (verifiedRoutingDetails.bankZip) setBankZip(verifiedRoutingDetails.bankZip);
        if (verifiedRoutingDetails.bankCountry) {
            const country = verifiedRoutingDetails.bankCountry;
            const normalizedCountry = country.length === 2 ? country.toUpperCase() : "US";
            setBankCountry(normalizedCountry);
            setBankCountryCode(normalizedCountry);
        }
    }, [verifiedRoutingDetails]);

    // hydrate fields from saved state
    useEffect(() => {
        if (isCryptoFlow) return;

        if (isCryptoToFiatFlow && savedCryptoFiat) {
            setSelectedBank(
                savedCryptoFiat.institution
                    ? { code: savedCryptoFiat.institution, name: savedCryptoFiat.bankName || "" }
                    : selectedBank
            );
            setAccountNumber(savedCryptoFiat.accountIdentifier || "");
            setAccountName(savedCryptoFiat.accountName || "");
            return;
        }

        if (isBankRecipient && savedBank) {
            setSelectedBank(
                savedBank.bankCode
                    ? { code: savedBank.bankCode, name: savedBank.bankName || "" }
                    : null
            );
            setAccountNumber(savedBank.accountNumber || "");
            setAccountName(savedBank.name || "");
            setSwiftCode(savedBank.swiftCode || "");
            return;
        }

        if (!isBankRecipient && savedInternal) {
            setAccountNumber(savedInternal.accountNumber || "");
            setAccountName(savedInternal.name || "");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isBankRecipient, isCryptoToFiatFlow]);

    useEffect(() => {
        if (!isUSD || isIBANFlow || isSwiftFlow) return;

        dispatch(
            setUsdAll({
                recipient: {
                    bank_name: bankName || selectedBank?.name || "",
                    // API flags
                    account_type: "us",
                    account_owner_type: holderType || "",
                    // API name fields
                    first_name: holderType === "individual" ? firstName : "",
                    last_name: holderType === "individual" ? lastName : "",
                    business_name: holderType === "corporate" ? businessName : "",
                    // Back-compat convenience
                    account_owner_name: accountName || "",
                    // Bank account
                    account: {
                        account_number: accountNumber || "",
                        routing_number: routingNumber || "",
                        checking_or_savings: checkingOrSavings || "",
                    },
                    // Address (use 'US' per API sample)
                    address: {
                        street_line_1: recStreet || "",
                        city: recCity || "",
                        state: recState || "",
                        postal_code: recZip || "",
                        country: recCountry || "US",
                    },
                    bank_address: {
                        street_line_1: bankStreet || "",
                        city: bankCity || "",
                        state: bankState || "",
                        postal_code: bankZip || "",
                        country: bankCountry || "US",
                    },
                    relationship: relationship || "",
                    email: recipientEmail || "",
                },
            })
        );
    }, [
        isUSD,
        isIBANFlow,
        isSwiftFlow,
        bankName,
        selectedBank?.name,
        holderType,
        firstName,
        lastName,
        businessName,
        accountName,
        accountNumber,
        routingNumber,
        checkingOrSavings,
        recStreet,
        recCountry,
        recCity,
        recState,
        recZip,
        bankStreet,
        bankCountry,
        bankCity,
        bankState,
        bankZip,
        relationship,
        recipientEmail,
        dispatch,
    ]);

    useEffect(() => {
        if (!isIBANFlow) return;

        const composedName = (accountName || "").trim();

        dispatch(
            setUsdAll({
                recipient: {
                    bank_name: bankName || selectedBank?.name || "",
                    account_type: "iban",
                    account_owner_type: holderType || "",
                    first_name: holderType === "individual" ? firstName : "",
                    last_name: holderType === "individual" ? lastName : "",
                    business_name: holderType === "corporate" ? businessName : "",
                    account_owner_name: composedName,
                    // IBAN payload
                    iban: {
                        account_number: iban || "",
                        bic: bic || "",
                        country: bankCountryCode || "",
                    },
                    // Set address + country to bank country
                    address: {
                        street_line_1: recStreet || "",
                        city: recCity || "",
                        state: recState || "",
                        postal_code: recZip || "",
                        country: recCountry || "",
                    },
                    relationship: relationship || "",
                    email: recipientEmail || "",
                },
            })
        );
    }, [
        isIBANFlow,
        bankName,
        selectedBank?.name,
        holderType,
        firstName,
        lastName,
        businessName,
        accountName,
        iban,
        bic,
        bankCountryCode,
        recStreet,
        recCountry,
        recCity,
        recState,
        recZip,
        relationship,
        recipientEmail,
        dispatch,
    ]);

    useEffect(() => {
        if (!isSwiftFlow) return;

        const composedName =
            holderType === "corporate"
                ? businessName
                : [firstName, lastName].filter(Boolean).join(" ").trim();

        dispatch(
            setUsdAll({
                recipient: {
                    bank_name: bankName || selectedBank?.name || "",
                    account_type: "international_wire",
                    account_owner_type: holderType || "",
                    first_name: holderType === "individual" ? firstName : "",
                    last_name: holderType === "individual" ? lastName : "",
                    business_name: holderType === "corporate" ? businessName : "",
                    account_owner_name: composedName,
                    // SWIFT payload
                    swift: {
                        account_number: accountNumber || "",
                        routing_number: routingNumber || "",
                        bic: bic || "",
                        country: bankCountryCode || "",
                    },
                    // Address at destination bank country
                    address: {
                        street_line_1: recStreet || "",
                        city: recCity || "",
                        state: recState || "",
                        postal_code: recZip || "",
                        country: bankCountryCode || "",
                    },
                },
            })
        );
    }, [
        isSwiftFlow,
        bankName,
        selectedBank?.name,
        holderType,
        firstName,
        lastName,
        businessName,
        accountNumber,
        routingNumber,
        bic,
        bankCountryCode,
        recStreet,
        recCity,
        recState,
        recZip,
        dispatch,
    ]);

    const handleNetworkPicked = (net) => {
        const label = net?.label || net?.name || net?.id || "";
        setCryptoNetworkLocal(label);
        dispatch(setCryptoNetwork(label));
        closeNetworkPicker();
    };

    const handleAddressChange = (e) => {
        const v = e.target.value;
        setCryptoAddressLocal(v);
        dispatch(setCryptoAddress(v));
    };

    useEffect(() => {
        if (!isBankRecipient) return;
        dispatch(
            setBankRecipient({
                name: accountName || "",
                accountNumber: accountNumber || "",
                bankName: selectedBank?.name || "",
                bankCode: selectedBank?.code || "",
                swiftCode: swiftCode || "",
            })
        );
    }, [
        isBankRecipient,
        accountName,
        accountNumber,
        selectedBank?.code,
        selectedBank?.name,
        swiftCode,
        dispatch,
    ]);

    // LIVE-SAVE: crypto→fiat recipient fields
    useEffect(() => {
        if (!isCryptoToFiatFlow) return;
        dispatch(
            setCryptoFiatRecipient({
                institution: selectedBank?.code || "",
                bankName: selectedBank?.name || "",
                accountIdentifier: accountNumber || "",
                accountName: accountName || "",
            })
        );
    }, [
        isCryptoToFiatFlow,
        selectedBank?.code,
        selectedBank?.name,
        accountNumber,
        accountName,
        dispatch,
    ]);

    // LIVE-SAVE: internal recipient fields
    useEffect(() => {
        if (isBankRecipient || isCryptoFlow || isCryptoToFiatFlow) return;
        dispatch(setRecipient({ name: accountName || "", accountNumber: accountNumber || "" }));
    }, [isBankRecipient, isCryptoFlow, isCryptoToFiatFlow, accountName, accountNumber, dispatch]);

    // Account number → auto-verify (bank / internal)
    useEffect(() => {
        if (isUSD) {
            setLoading(false);
            return;
        }

        if (isKES && (isBankRecipient || isCryptoToFiatFlow)) {
            setLoading(false);
            return;
        }

        const digits = String(accountNumber).replace(/\D/g, "");
        const has9orMore = /^\d{10,}$/.test(digits);
        if (!has9orMore) {
            setAccountName("");
            setLoading(false);
            return;
        }

        if ((isBankRecipient || isCryptoToFiatFlow) && !selectedBank?.code) {
            setAccountName("");
            setLoading(false);
            return;
        }

        const controller = new AbortController();
        const timer = setTimeout(async () => {
            try {
                setLoading(true);

                if (isBankRecipient || isCryptoToFiatFlow) {
                    const payload = new URLSearchParams({
                        accountNumber,
                        currency: validationCurrencyFrom,
                        currencyTo: validationCurrencyTo,
                        bankCode: selectedBank?.code || "",
                        ...(verificationSource ? { source: verificationSource } : {}),
                    });
                    const res = await axios.post("/v1/wallets/verifyBankDetail", payload, {
                        headers: {
                            "Content-Type": "application/x-www-form-urlencoded",
                            "x-business-id": businessId,
                        },
                        signal: controller.signal,
                    });
                    const verifiedName = res?.data?.data?.accountName || "Account not found";
                    setAccountName(verifiedName);

                    if (isBankRecipient) {
                        dispatch(
                            setBankRecipient({
                                name: verifiedName || "",
                                accountNumber,
                                bankName: selectedBank?.name || "",
                                bankCode: selectedBank?.code || "",
                                swiftCode: swiftCode || "",
                            })
                        );
                    } else {
                        dispatch(
                            setCryptoFiatRecipient({
                                institution: selectedBank?.code || "",
                                bankName: selectedBank?.name || "",
                                accountIdentifier: accountNumber,
                                accountName: verifiedName || "",
                            })
                        );
                    }
                } else {
                    const res = await axios.get("/v1/wallets/get_wallet_by_account_number/", {
                        params: { currency, accountNumber },
                        headers: { "x-business-id": businessId },
                        signal: controller.signal,
                    });
                    const businessNameFound = res?.data?.data?.businessName || "Account not found";
                    setAccountName(businessNameFound);
                    dispatch(
                        setRecipient({
                            name: businessNameFound || "",
                            accountNumber,
                        })
                    );
                }
            } catch (err) {
                if (err.name !== "CanceledError") {
                    console.error("Failed to validate account number:", err);
                    setAccountName("Account not found");
                }
            } finally {
                setLoading(false);
            }
        }, 300);

        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [
        accountNumber,
        currency,
        isUSD,
        validationCurrencyFrom,
        validationCurrencyTo,
        businessId,
        isBankRecipient,
        isCryptoToFiatFlow,
        isKES,
        selectedBank?.code,
        selectedBank?.name,
        verificationSource,
        dispatch,
        swiftCode,
    ]);

    const handleSubmit = () => {
        if (!canContinue) return;

        if (isCryptoFlow) {
            navigate(nextRoute);
            return;
        }

        if (isUSD) {
            const ownerTypeForApi =
                holderType === "corporate"
                    ? "corporate"
                    : holderType === "individual"
                      ? "individual"
                      : "";

            const iscorporate = ownerTypeForApi === "corporate";
            const isIndividual = ownerTypeForApi === "individual";

            const composedName = (accountName || "").trim();

            const accountTypeForApi = isIBANFlow
                ? "iban"
                : isSwiftFlow
                  ? "international_wire"
                  : "us";

            const recipientBase = {
                bank_name: bankName || selectedBank?.name || "",
                account_type: accountTypeForApi,
                account_owner_type: ownerTypeForApi,
                first_name: isIndividual ? firstName : "",
                last_name: isIndividual ? lastName : "",
                business_name: iscorporate ? businessName : "",
                account_owner_name: composedName,
                address: {
                    street_line_1: recStreet || "",
                    city: recCity || "",
                    state: recState || "",
                    postal_code: recZip || "",
                    country: recCountry || "",
                },
                relationship: relationship || "",
                email: recipientEmail || "",
                ...(!isIBANFlow && !isSwiftFlow
                    ? {
                          bank_address: {
                              street_line_1: bankStreet || "",
                              city: bankCity || "",
                              state: bankState || "",
                              postal_code: bankZip || "",
                              country: bankCountry || "US",
                          },
                      }
                    : {}),
            };

            // attach account payload per method
            let methodBlock = {};
            if (accountTypeForApi === "iban") {
                methodBlock = {
                    iban: {
                        account_number: iban || "",
                        bic: bic || "",
                        country: bankCountryCode || "",
                    },
                };
            } else if (accountTypeForApi === "international_wire") {
                methodBlock = {
                    swift: {
                        account_number: accountNumber || "",
                        routing_number: routingNumber || "",
                        bic: bic || "",
                        country: bankCountryCode || "",
                    },
                };
            } else {
                methodBlock = {
                    account: {
                        account_number: accountNumber || "",
                        routing_number: routingNumber || "",
                        checking_or_savings: checkingOrSavings || "",
                    },
                };
            }

            dispatch(
                setUsdAll({
                    accountOwnerType: ownerTypeForApi,
                    accountType: accountTypeForApi,
                    bankName: selectedBank?.name || "",
                    accountOwnerName: composedName,
                    firstName: isIndividual ? firstName : "",
                    lastName: isIndividual ? lastName : "",
                    businessName: iscorporate ? businessName : "",

                    recipient: {
                        ...recipientBase,
                        ...methodBlock,
                    },
                })
            );

            navigate(nextRoute);
            return;
        }

        if (!canContinueBank) return;

        if (isCryptoToFiatFlow) {
            dispatch(
                setCryptoFiatRecipient({
                    institution: selectedBank?.code || "",
                    accountIdentifier: accountNumber,
                    bankName: selectedBank?.name || "",
                    accountName,
                })
            );
        } else if (isBankRecipient) {
            dispatch(
                setBankRecipient({
                    name: accountName,
                    accountNumber,
                    bankName: selectedBank?.name || "",
                    bankCode: selectedBank?.code || "",
                    swiftCode: swiftCode || "",
                })
            );
        } else {
            dispatch(setRecipient({ name: accountName, accountNumber }));
        }

        navigate(nextRoute);
    };

    const networkDisplayName = (() => {
        if (typeof cryptoNetwork === "string") return `${cryptoNetwork}`.trim();
        return `${cryptoNetwork?.label ?? ""}${
            cryptoNetwork?.name ? ` (${cryptoNetwork.name})` : ""
        }`;
    })();

    const needsBankPick = (isBankRecipient || isCryptoToFiatFlow) && !isUSD;
    const nameOk = isKES ? !!accountName : !!accountName && accountName !== "Account not found";
    const canContinueBank =
        !!accountNumber &&
        nameOk &&
        !!accountName &&
        accountName !== "Account not found" &&
        (!needsBankPick || !!selectedBank?.code);

    const canContinueCrypto = isCryptoFlow && !!cryptoAddress && !!cryptoNetwork;
    const holderOk =
        holderType === "individual"
            ? !!firstName && !!lastName
            : holderType === "corporate"
              ? !!businessName
              : false;

    const canContinueUSD =
        isUSD &&
        !isIBANFlow &&
        !isSwiftFlow &&
        !!holderType &&
        holderOk &&
        !!bankName &&
        !!accountType &&
        !!routingNumber &&
        !!accountNumber &&
        !!accountName &&
        !!recStreet &&
        !!recCountry &&
        !!recCity &&
        !!recState &&
        !!recZip &&
        !!bankStreet &&
        !!bankCountry &&
        !!bankCity &&
        !!bankState &&
        !!bankZip &&
        !!relationship &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail);

    const canContinueIBAN =
        isIBANFlow &&
        !!holderType &&
        holderOk &&
        !!bankName &&
        !!bankCountryCode &&
        !!iban &&
        !!bic &&
        !!accountName &&
        !!relationship &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail) &&
        !!recStreet &&
        !!recCountry &&
        !!recCity &&
        !!recState &&
        !!recZip;

    const canContinueSWIFT =
        isSwiftFlow &&
        !!holderType &&
        holderOk &&
        !!bankName &&
        !!bankCountryCode &&
        !!accountNumber &&
        !!bic &&
        !!recStreet &&
        !!recCity &&
        !!recState &&
        !!recZip;

    const canContinue = isCryptoFlow
        ? canContinueCrypto
        : isUSD
          ? isIBANFlow
              ? canContinueIBAN
              : isSwiftFlow
                ? canContinueSWIFT
                : canContinueUSD
          : canContinueBank;

    const ACCOUNT_TYPE_OPTIONS = [
        { value: "checking", label: "Checking" },
        { value: "savings", label: "Savings" },
    ];

    return (
        <div className="space-y-6">
            <h1 className="text-2xl font-medium text-[#101828]">Add recipient</h1>

            {((isBankRecipient && !isUSD) || isCryptoToFiatFlow) && (
                <BankPickerCard
                    selectedBank={selectedBank}
                    onClick={() => setShowBankModal(true)}
                />
            )}

            {isCryptoFlow ? (
                <CryptoRecipientSection
                    networkDisplayName={networkDisplayName}
                    cryptoAddress={cryptoAddress}
                    currency={currency}
                    onAddressChange={handleAddressChange}
                />
            ) : currency === "USD" ? (
                <>
                    <Input
                        disabled
                        required={false}
                        label="Recipient location"
                        labelClassName="!mt-0 text-[#101828]"
                        value={recipientLocationLabel}
                        className="!mt-0"
                    />
                    <Input
                        disabled
                        required={false}
                        label="Payment method"
                        labelClassName="!mt-0 text-[#101828]"
                        value={paymentMethodLabel}
                        className="!mt-0"
                    />
                    {!isIBANFlow && (
                        <UsdRoutingNumberField
                            error={routingVerificationError}
                            loading={routingVerificationLoading}
                            routingNumber={routingNumber}
                            setRoutingNumber={setRoutingNumber}
                        />
                    )}
                    {isIBANFlow ? (
                        <UsdIbanSection
                            bankCountryCode={bankCountryCode}
                            setBankCountryCode={setBankCountryCode}
                            bankName={bankName}
                            setBankName={setBankName}
                            accountName={accountName}
                            setAccountName={setAccountName}
                            holderType={holderType}
                            setHolderType={(v) => {
                                setHolderType(v);
                                setFirstName("");
                                setLastName("");
                                setBusinessName("");
                            }}
                            firstName={firstName}
                            setFirstName={setFirstName}
                            lastName={lastName}
                            setLastName={setLastName}
                            businessName={businessName}
                            setBusinessName={setBusinessName}
                            recStreet={recStreet}
                            setRecStreet={setRecStreet}
                            recCountry={recCountry}
                            setRecCountry={setRecCountry}
                            recCity={recCity}
                            setRecCity={setRecCity}
                            recState={recState}
                            setRecState={setRecState}
                            recZip={recZip}
                            setRecZip={setRecZip}
                            iban={iban}
                            setIban={setIban}
                            bic={bic}
                            setBic={setBic}
                            relationship={relationship}
                            setRelationship={setRelationship}
                            recipientEmail={recipientEmail}
                            setRecipientEmail={setRecipientEmail}
                        />
                    ) : isSwiftFlow ? (
                        <UsdSwiftSection
                            COUNTRY_OPTIONS={COUNTRY_OPTIONS}
                            bankCountryCode={bankCountryCode}
                            setBankCountryCode={setBankCountryCode}
                            selectedBank={selectedBank}
                            onOpenBankModal={() => setShowBankModal(true)}
                            bankName={bankName}
                            setBankName={setBankName}
                            holderType={holderType}
                            setHolderType={(v) => {
                                setHolderType(v);
                                setFirstName("");
                                setLastName("");
                                setBusinessName("");
                            }}
                            firstName={firstName}
                            setFirstName={setFirstName}
                            lastName={lastName}
                            setLastName={setLastName}
                            businessName={businessName}
                            setBusinessName={setBusinessName}
                            recStreet={recStreet}
                            setRecStreet={setRecStreet}
                            recCity={recCity}
                            setRecCity={setRecCity}
                            recState={recState}
                            setRecState={setRecState}
                            recZip={recZip}
                            setRecZip={setRecZip}
                            accountNumber={accountNumber}
                            setAccountNumber={setAccountNumber}
                            routingNumber={routingNumber}
                            setRoutingNumber={setRoutingNumber}
                            bic={bic}
                            setBic={setBic}
                            verifiedBankDetails={verifiedRoutingDetails}
                        />
                    ) : (
                        <UsdAchSection
                            holderType={holderType}
                            setHolderType={(v) => {
                                setHolderType(v);
                                setFirstName("");
                                setLastName("");
                                setBusinessName("");
                            }}
                            firstName={firstName}
                            setFirstName={setFirstName}
                            lastName={lastName}
                            setLastName={setLastName}
                            businessName={businessName}
                            setBusinessName={setBusinessName}
                            accountType={accountType}
                            setAccountType={setAccountType}
                            ACCOUNT_TYPE_OPTIONS={ACCOUNT_TYPE_OPTIONS}
                            routingNumber={routingNumber}
                            setRoutingNumber={setRoutingNumber}
                            accountNumber={accountNumber}
                            setAccountNumber={setAccountNumber}
                            accountName={accountName}
                            setAccountName={setAccountName}
                            bankName={bankName}
                            setBankName={setBankName}
                            bankStreet={bankStreet}
                            setBankStreet={setBankStreet}
                            bankCountry={bankCountry}
                            setBankCountry={setBankCountry}
                            bankState={bankState}
                            setBankState={setBankState}
                            bankCity={bankCity}
                            setBankCity={setBankCity}
                            bankZip={bankZip}
                            setBankZip={setBankZip}
                            relationship={relationship}
                            setRelationship={setRelationship}
                            recipientEmail={recipientEmail}
                            setRecipientEmail={setRecipientEmail}
                            recStreet={recStreet}
                            setRecStreet={setRecStreet}
                            recCountry={recCountry}
                            setRecCountry={setRecCountry}
                            recCity={recCity}
                            setRecCity={setRecCity}
                            recState={recState}
                            setRecState={setRecState}
                            recZip={recZip}
                            setRecZip={setRecZip}
                            verifiedBankDetails={verifiedRoutingDetails}
                        />
                    )}
                </>
            ) : (
                <NonUsdRecipientSection
                    isKES={isKES}
                    accountNumber={accountNumber}
                    setAccountNumber={setAccountNumber}
                    accountName={accountName}
                    setAccountName={setAccountName}
                    loading={loading}
                    MIN_DIGITS={MIN_DIGITS}
                    MAX_DIGITS={MAX_DIGITS}
                />
            )}

            <div
                className={`flex ${
                    !isCryptoFlow ? "justify-between items-center" : "justify-end"
                } mt-8`}
            >
                {!isCryptoFlow && (
                    <button
                        className="text-[#101828] flex items-center gap-2"
                        onClick={() => onBack("Partners")}
                    >
                        <MdArrowBackIos />{" "}
                        {isCryptoCurrency || isBankRecipient
                            ? "Select from beneficiaries"
                            : "select from partners"}
                    </button>
                )}

                <div className="flex gap-8 items-center mt-2">
                    <button className="text-[#101828]" onClick={() => navigate(-1)}>
                        Back
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={!canContinue}
                        className={`rounded-lg px-6 py-2 font-semibold text-white transition ${
                            canContinue
                                ? "bg-[#6F00FF] hover:bg-[#5A00D6]"
                                : "bg-[#E9D7FE] text-[#B3A8DD] cursor-not-allowed"
                        }`}
                    >
                        {isUSD ? "Confirm" : "Continue"}
                    </button>
                </div>
            </div>
            <SelectNetworkModal
                isOpen={showNetworkModal}
                onClose={closeNetworkPicker}
                onSelect={handleNetworkPicked}
                selectedId={cryptoNetwork?.id}
            />

            <ChooseBankModal
                isOpen={showBankModal}
                onClose={() => setShowBankModal(false)}
                onSelectBank={(bank) => {
                    setSelectedBank(bank);
                    if (isBankRecipient) {
                        dispatch(
                            setBankRecipient({
                                name: accountName || "",
                                accountNumber,
                                bankName: bank?.name || "",
                                bankCode: bank?.code || "",
                                swiftCode: swiftCode || "",
                            })
                        );
                    }
                    if (isUSD) {
                        setBankName(bank?.name || "");
                        dispatch(
                            setUsdAll({
                                recipient: {
                                    bank_name: bank?.name || "",
                                },
                            })
                        );
                    } else if (isCryptoToFiatFlow) {
                        dispatch(
                            setCryptoFiatRecipient({
                                institution: bank?.code || "",
                                bankName: bank?.name || "",
                                accountIdentifier: accountNumber || "",
                                accountName: accountName || "",
                            })
                        );
                    }
                }}
                recipientCurrency={recipientCurrency}
            />
        </div>
    );
}
